import React, { useContext, useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import StarekContext from "../context/StarekContext";
import SearchResults from "../components/modals/SearchResults";
import SupportModal from "../components/modals/SupportModal";
import { gql, useQuery, useMutation } from "@apollo/client";
import moment from "moment-timezone";

function Header(props) {
    const { hasLeftMargin = true } = props;
    const GET_USER_ACTIVITIES_QUERY = gql`
        query User($where: UserWhereUniqueInput!) {
            user(where: $where) {
                id
                name
                email
                home_timezone
                show_time_in_home_timezone
                username
                verified
                memberships {
                    role {
                        name
                    }
                }
            }
        }
    `;
    const UPDATE_USER_PROFILE = gql`
        mutation UpdateUser(
            $where: UserWhereUniqueInput!
            $data: UserUpdateInput!
        ) {
            updateOneUser(where: $where, data: $data) {
                id
                home_timezone
                show_time_in_home_timezone
            }
        }
    `;
    const starekContext = useContext(StarekContext);
    const location = useLocation();

    const { data: userData, loading: userLoading } = useQuery(
        GET_USER_ACTIVITIES_QUERY,
        {
            variables: {
                where: { id: parseInt(starekContext?.user?.id) },
            },
        },
    );

    const [updateUserProfile, { loading: updatingProfile }] =
        useMutation(UPDATE_USER_PROFILE);

    const [selected, setSelected] = useState(false);
    const [item, setItem] = useState({});
    const [timeZones, setTimeZones] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [showSupportModal, setShowSupportModal] = useState(
        location.hash === "#support",
    );
    const [searchText, setSearchText] = useState("");
    const [activities, setActivities] = useState([]);
    const [totalActivity, setTotalActivity] = useState(0);
    const [types, setTypes] = useState([]);
    const [noMore, setNoMore] = useState(true);
    const [page, setPage] = useState(2);
    const profilePanelRef = useRef(null);
    const profileButtonRef = useRef(null);

    useEffect(() => {
        if (!userData?.user) return;
        setItem(userData.user);
    }, [userData?.user]);

    useEffect(() => {
        setTimeZones(moment.tz.names());
    }, []);

    useEffect(() => {
        if (!selected) return;
        const handleOutsideClick = (event) => {
            if (
                profilePanelRef.current?.contains(event.target) ||
                profileButtonRef.current?.contains(event.target)
            ) {
                return;
            }
            setSelected(false);
        };
        document.addEventListener("mousedown", handleOutsideClick);
        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
        };
    }, [selected]);

    const toggleSearchModal = (value) => setShowModal(value);

    useEffect(() => {
        setShowSupportModal(location.hash === "#support");
    }, [location.hash]);

    const openSupportModal = () => {
        navigator(`${location.pathname}${location.search}#support`);
    };

    const closeSupportModal = () => {
        navigator(`${location.pathname}${location.search}`, {
            replace: true,
        });
    };

    const handleSwitchWorkspace = () => {
        sessionStorage.setItem(
            "postAuthRedirect",
            window.location.pathname + window.location.search,
        );
        navigator("/choose-workspace");
    };

    const navigator = useNavigate();
    return (
        <>
            <nav
                className={`main-header navbar navbar-expand navbar-white navbar-light starek-header-nav ${
                    hasLeftMargin ? "starek-header-with-margin" : ""
                }`}
            >
                {/* Workspace & Plant Section - Flattened */}
                <div className="starek-header-workspace">
                    {/* Workspace Logo & Name */}
                    <div
                        className="starek-header-workspace-link"
                        onClick={handleSwitchWorkspace}
                    >
                        {starekContext?.lastUsedWorkspace?.logo && (
                            <div className="starek-header-workspace-logo">
                                <img
                                    src={starekContext?.lastUsedWorkspace?.logo}
                                    alt="Workspace Logo"
                                    className="starek-header-workspace-logo-img"
                                />
                            </div>
                        )}
                        <span className="starek-header-workspace-name">
                            {starekContext?.lastUsedWorkspace?.name}
                        </span>
                    </div>
                </div>

                <ul className="navbar-nav ml-auto">
                    {/* User Profile & Logout Section */}
                    <li className="nav-item starek-header-user-nav-item">
                        <div className="starek-header-user-wrap">
                            {/* User Profile Button */}
                            <div
                                className="starek-header-user-button"
                                onClick={() => {
                                    setSelected((prev) => !prev);
                                }}
                                ref={profileButtonRef}
                            >
                                <div className="starek-header-avatar">
                                    <i className="fa fa-user starek-header-avatar-icon"></i>
                                    {totalActivity > 0 && (
                                        <span className="starek-header-activity-badge">
                                            {totalActivity}
                                        </span>
                                    )}
                                </div>
                                <div className="starek-header-user-meta">
                                    <span className="starek-header-user-name">
                                        {starekContext.user?.name || "User"}
                                    </span>
                                </div>
                            </div>

                            {selected && (
                                <div
                                    ref={profilePanelRef}
                                    role="dialog"
                                    aria-label="User profile"
                                    className="starek-profile-panel"
                                >
                                    <div className="starek-profile-panel-header">
                                        <div className="starek-profile-panel-title">
                                            <span className="starek-profile-panel-name">
                                                {starekContext.user?.name}
                                            </span>
                                            <span className="starek-profile-panel-email">
                                                {starekContext.user?.email ||
                                                    ""}
                                            </span>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setSelected(false)}
                                            className="starek-profile-panel-close"
                                            aria-label="Close profile panel"
                                        >
                                            <i className="fa fa-times"></i>
                                        </button>
                                    </div>
                                    <div className="form-group">
                                        <label>Home Time Zone</label>
                                        <select
                                            className="form-control"
                                            id="homeTimeZoneId"
                                            name="homeTimeZoneId"
                                            value={item?.home_timezone || ""}
                                            onChange={(e) => {
                                                setItem({
                                                    ...item,
                                                    home_timezone:
                                                        e.target.value,
                                                });
                                            }}
                                        >
                                            <option value="0">
                                                --Select--
                                            </option>
                                            {timeZones.map((s, i) => (
                                                <option key={i} value={s}>
                                                    {s}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>Always Use Home Timezone</label>
                                        <input
                                            type="checkbox"
                                            className="ml-3"
                                            id="show_time_in_home_timezone"
                                            name="show_time_in_home_timezone"
                                            checked={
                                                !!item?.show_time_in_home_timezone
                                            }
                                            onChange={() => {
                                                setItem({
                                                    ...item,
                                                    show_time_in_home_timezone:
                                                        !item?.show_time_in_home_timezone,
                                                });
                                            }}
                                        ></input>
                                        <br />
                                        <small>
                                            <i>
                                                Enabling this will{" "}
                                                <strong>not</strong> show times
                                                in your current timezone
                                            </i>
                                        </small>
                                    </div>
                                    <div className="starek-profile-panel-actions">
                                        <button
                                            className="btn btn-sm btn-info btn-primary"
                                            disabled={
                                                userLoading ||
                                                updatingProfile ||
                                                !item?.id
                                            }
                                            onClick={async () => {
                                                if (!item?.id) return;
                                                const homeTimezoneValue =
                                                    item?.home_timezone || null;
                                                const result =
                                                    await updateUserProfile({
                                                        variables: {
                                                            where: {
                                                                id: parseInt(
                                                                    item.id,
                                                                ),
                                                            },
                                                            data: {
                                                                home_timezone: {
                                                                    set: homeTimezoneValue,
                                                                },
                                                                show_time_in_home_timezone:
                                                                    {
                                                                        set: !!item?.show_time_in_home_timezone,
                                                                    },
                                                            },
                                                        },
                                                    });
                                                if (
                                                    result?.data?.updateOneUser
                                                ) {
                                                    setItem(
                                                        result.data
                                                            .updateOneUser,
                                                    );
                                                }
                                            }}
                                        >
                                            {updatingProfile
                                                ? "Saving..."
                                                : "Update"}
                                        </button>
                                        <button
                                            className="btn btn-sm btn-info"
                                            onClick={() => {
                                                setSelected(false);
                                            }}
                                        >
                                            Close
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Support Link */}
                            <span
                                className="nav-link starek-header-support"
                                role="button"
                                title="Support"
                                onClick={openSupportModal}
                            >
                                <div className="starek-header-support-icon">
                                    <i className="fa fa-headset starek-header-logout-icon-inner"></i>
                                </div>
                            </span>

                            {/* Logout Button */}
                            <span
                                className="nav-link starek-header-logout"
                                role="button"
                                title="Logout"
                                onClick={async () => {
                                    await starekContext.logout();
                                }}
                            >
                                <div className="starek-header-logout-icon">
                                    <i className="fa fa-sign-out-alt starek-header-logout-icon-inner"></i>
                                </div>
                            </span>
                        </div>
                    </li>
                </ul>
            </nav>
            {showModal && (
                <SearchResults
                    visibility={showModal}
                    initialSearchText={searchText}
                    setVisibility={toggleSearchModal}
                    setInitialSearchText={setSearchText}
                />
            )}
            <SupportModal
                open={showSupportModal}
                onClose={closeSupportModal}
            />
        </>
    );
}

export default Header;
